<script>
import Modal from '@/components/Modal.vue'
import { getIntegrationKeys, deactivateIntegration } from '@/services/integrationServices'
import IntegrationRegister from './components/IntegrationRegister.vue'
import { KeyIcon, ChevronLeft, ChevronRight } from 'lucide-vue-next'

export default {
  components: {
    Modal,
    IntegrationRegister,
    KeyIcon,
    ChevronLeft,
    ChevronRight,
  },
  data() {
    return {
      loading: false,
      search: '',
      isRegister: false,
      integrationKeys: [],
      visibleKeys: new Set(),
      statusFilter: 'active',
      sortField: 'createdAt',
      sortDirection: 'desc',
      currentPage: 1,
      pageLimit: 10,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        total_pages: 0,
        active_total: 0,
        inactive_total: 0,
      },
      searchDebounceId: null,
      modal: {
        deactivateIntegration: false,
      },
      hold: {
        integrationKeyId: '',
      },
    }
  },
  computed: {
    filteredIntegrationKeys() {
      return this.integrationKeys || [];
    },
    sortedIntegrationKeys() {
      return this.filteredIntegrationKeys;
    },
    paginatedKeys() {
      return this.sortedIntegrationKeys;
    },
    totalPages() {
      const totalPages = Number(this.pagination?.total_pages || 0);
      return Math.max(1, totalPages);
    },
    visiblePageNumbers() {
      if (this.totalPages <= 5) {
        return Array.from({ length: this.totalPages }, (_, index) => ({
          key: `page-${index + 1}`,
          label: index + 1,
          value: index + 1,
          isEllipsis: false,
        }))
      }

      const pages = []
      const startPage = Math.max(1, this.currentPage - 1)
      const endPage = Math.min(this.totalPages, this.currentPage + 1)

      pages.push({
        key: 'page-1',
        label: 1,
        value: 1,
        isEllipsis: false,
      })

      if (startPage > 2) {
        pages.push({
          key: 'ellipsis-start',
          label: '...',
          value: null,
          isEllipsis: true,
        })
      }

      for (let page = startPage; page <= endPage; page += 1) {
        if (page !== 1 && page !== this.totalPages) {
          pages.push({
            key: `page-${page}`,
            label: page,
            value: page,
            isEllipsis: false,
          })
        }
      }

      if (endPage < this.totalPages - 1) {
        pages.push({
          key: 'ellipsis-end',
          label: '...',
          value: null,
          isEllipsis: true,
        })
      }

      pages.push({
        key: `page-${this.totalPages}`,
        label: this.totalPages,
        value: this.totalPages,
        isEllipsis: false,
      })

      return pages
    },
    activeCount() {
      return Number(this.pagination?.active_total || 0);
    },
    totalKeyCount() {
      return Number(this.pagination?.active_total || 0) + Number(this.pagination?.inactive_total || 0);
    },
  },
  mounted() {
    this.fetchIntegrationKeys()
  },
  methods: {
    toggleSort(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'desc';
      }
      this.currentPage = 1
      this.fetchIntegrationKeys()
    },
    isKeyVisible(keyId) {
      return this.visibleKeys.has(keyId)
    },
    toggleKeyVisibility(keyId) {
      if (this.visibleKeys.has(keyId)) {
        this.visibleKeys.delete(keyId)
      } else {
        this.visibleKeys.add(keyId)
      }
    },
    async fetchIntegrationKeys() {
      try {
        this.loading = true
        const params = {
          page: this.currentPage,
          limit: this.pageLimit,
          sort_by: 'created_at',
          sort_order: this.sortDirection,
        }

        const trimmedSearch = (this.search || '').trim()
        if (trimmedSearch.length >= 2) {
          params.q = trimmedSearch
        }

        if (this.statusFilter !== 'all') {
          params.is_active = this.statusFilter === 'active'
        }

        const response = await getIntegrationKeys(params)

        this.integrationKeys = response.data || []
        this.pagination = response.pagination || this.pagination
      } catch (error) {
        console.error('Error fetching integration keys:', error)
        this.integrationKeys = []
        this.pagination = {
          page: 1,
          limit: this.pageLimit,
          total: 0,
          total_pages: 0,
          active_total: 0,
          inactive_total: 0,
        }
      } finally {
        this.loading = false
      }
    },
    onSearchInput() {
      if (this.searchDebounceId) {
        clearTimeout(this.searchDebounceId)
      }
      this.currentPage = 1
      this.searchDebounceId = setTimeout(() => {
        this.fetchIntegrationKeys()
      }, 300)
    },
    onStatusChange() {
      this.currentPage = 1
      this.fetchIntegrationKeys()
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text)
    },
    convertToMalaysiaTime(utcDateTime) {
      const utcDate = new Date(`${utcDateTime}Z`);
      const malaysiaDate = new Date(utcDate.getTime() + 8 * 60 * 60 * 1000);
      return malaysiaDate.toISOString().replace('T', ' ').slice(0, 19);
    },
    formatDate(dateString) {
      if (!dateString) return '—';
      const convertedDate = this.convertToMalaysiaTime(dateString);
      return new Date(convertedDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    async deactivateKey(keyID){
      if (!keyID) {
        return
      }

      const response = await deactivateIntegration(keyID);
      if(response.status === 'success'){
        await this.fetchIntegrationKeys()
      }
    },
    askDeactivate(keyID) {
      this.hold.integrationKeyId = keyID
      this.modal.deactivateIntegration = true
    },
    async confirmDeactivate() {
      await this.deactivateKey(this.hold.integrationKeyId)
      this.closeDeactivate()
    },
    closeDeactivate() {
      this.modal.deactivateIntegration = false
      this.hold.integrationKeyId = ''
    },
    changePage(page) {
      this.currentPage = Math.min(Math.max(page, 1), this.totalPages);
      this.fetchIntegrationKeys()
    },
    maskedKey() {
      return '••••••••••••••••••••';
    },
  },
}
</script>

<template>
  <div class="admin-page wide">
    <Modal
      :isVisible="modal.deactivateIntegration"
      promptText="Deactivate this integration key?"
      yesText="Deactivate"
      noText="Cancel"
      @yes="confirmDeactivate()"
      @no="closeDeactivate()"
    />

    <div class="page-hero">
      <div>
        <p class="page-kicker">API Access</p>
        <h1 class="page-title"><KeyIcon :size="26" /> Integrations</h1>
        <p class="page-subtitle">Generate, inspect, copy, and deactivate integration keys in one controlled view.</p>
      </div>
      <button @click="isRegister = true" class="ui-button primary">
        <i class="fas fa-plus"></i> Generate key
      </button>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <span class="stat-label">Total keys</span>
        <strong class="stat-value">{{ totalKeyCount }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Active</span>
        <strong class="stat-value">{{ activeCount }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Filtered</span>
        <strong class="stat-value">{{ pagination.total }}</strong>
      </div>
    </div>

    <div class="ui-card module-filter-card">
      <div class="form-grid three">
        <div class="form-section">
          <label class="field-label">Search</label>
          <input v-model="search" type="text" placeholder="Search organization or user..." class="ui-input" @input="onSearchInput">
        </div>
        <div class="form-section">
          <label class="field-label">Status</label>
          <select v-model="statusFilter" class="ui-select" @change="onStatusChange">
            <option value="all">All status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div class="form-section">
          <label class="field-label">Created sort</label>
          <button class="ui-button secondary sort-button" @click="toggleSort('createdAt')">
            {{ sortDirection === 'desc' ? 'Newest first' : 'Oldest first' }}
            <i :class="['fas', sortDirection === 'desc' ? 'fa-sort-down' : 'fa-sort-up']"></i>
          </button>
        </div>
      </div>
    </div>

    <div class="table-card">
      <table class="ui-table mobile-card-table">
        <thead>
          <tr>
            <th>Organization</th>
            <th>User</th>
            <th>Integration key</th>
            <th>Created</th>
            <th>Last used</th>
            <th>Status</th>
            <th class="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="7">
              <div class="empty-state">Loading integration keys...</div>
            </td>
          </tr>
          <tr v-else v-for="key in paginatedKeys" :key="key.IntegrationKeyID">
            <td data-label="Organization">
              <span class="table-primary">{{ key.OrganizationName || 'Unknown organization' }}</span>
            </td>
            <td data-label="User">{{ key.UserName || '—' }}</td>
            <td data-label="Integration key">
              <div class="key-cell">
                <code class="key-code">{{ isKeyVisible(key.IntegrationKeyID) ? key.IntegrationKey : maskedKey() }}</code>
                <button @click="toggleKeyVisibility(key.IntegrationKeyID)" class="icon-button-soft" title="Show/hide key">
                  <i class="fa fa-eye"></i>
                </button>
                <button @click="copyToClipboard(key.IntegrationKey)" class="icon-button-soft" title="Copy key">
                  <i class="fa fa-clipboard"></i>
                </button>
              </div>
            </td>
            <td data-label="Created">{{ formatDate(key.CreatedAt) }}</td>
            <td data-label="Last used">{{ key.LastSentDate ? formatDate(key.LastSentDate) : 'Never' }}</td>
            <td data-label="Status">
              <span class="badge-soft" :class="key.IsActive ? 'success' : 'danger'">
                {{ key.IsActive ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td class="text-end" data-label="Actions">
              <button v-if="key.IsActive" @click="askDeactivate(key.IntegrationKeyID)" class="ui-button danger compact">
                Deactivate
              </button>
            </td>
          </tr>
          <tr v-if="!loading && paginatedKeys.length === 0">
            <td colspan="7">
              <div class="empty-state">No integration keys found.</div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="pagination-row" v-if="pagination.total">
        <span class="table-secondary">Page {{ currentPage }} of {{ totalPages }}</span>
        <div class="table-actions">
          <button class="icon-button-soft" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
            <ChevronLeft :size="16" />
          </button>
          <button
            v-for="page in visiblePageNumbers"
            :key="page.key"
            class="icon-button-soft"
            :disabled="page.isEllipsis || page.value === currentPage"
            :aria-current="page.value === currentPage ? 'page' : null"
            @click="page.value && changePage(page.value)"
          >
            {{ page.label }}
          </button>
          <button class="icon-button-soft" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">
            <ChevronRight :size="16" />
          </button>
        </div>
      </div>
    </div>

    <IntegrationRegister
      v-if="isRegister"
      :show="isRegister"
      @close="isRegister = false"
      @success="fetchIntegrationKeys"
    />
  </div>
</template>

<style scoped>
.page-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.module-filter-card {
  padding: 20px;
  margin-bottom: 18px;
}

.sort-button {
  width: 100%;
}

.key-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.key-code {
  max-width: 300px;
  padding: 6px 10px;
  border-radius: 8px;
  background: #f8fafc;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
}

.table-actions {
  display: flex;
  gap: 10px;
}

.compact {
  min-height: 34px;
  padding-inline: 12px;
  font-size: 0.8rem;
}
</style>
